import { createReducer } from '@reduxjs/toolkit'
import {
  Subscription,
  Package,
  ProductType,
  AnyProduct,
  Cart,
  DiscountedPrice,
  SummaryFormValues,
  Resource,
  PaymentMethod,
  AdministrativePayment,
} from 'shared/types'
import { initResource, setFailed, setPending, setSucceeded } from 'shared/utils'
import {
  selectProduct,
  setSummaryFormValues,
  getAvailableSubscriptions,
  getAdditionalPackages,
  applyPromoCode,
  removePromoCode,
  createOrder,
  getPaymentMethods,
  getAdministrativePaymentProduct,
} from './actions'

interface State {
  paymentMethods: Resource<PaymentMethod[]>
  subscriptions: Resource<Subscription[]>
  packages: Resource<Package[]>
  administrativePaymentProduct: Resource<AdministrativePayment>
  createOrder: Resource
  selectedProduct: AnyProduct | AdministrativePayment | null
  summaryFormValues: SummaryFormValues | null
  cart: Resource<Cart>
  applyPromoCode: Resource<DiscountedPrice | null>
  removePromoCode: Resource
}

const initialState: State = {
  paymentMethods: initResource([]),
  subscriptions: initResource([]),
  packages: initResource([]),
  administrativePaymentProduct: initResource(),
  createOrder: initResource(),
  selectedProduct: null,
  summaryFormValues: null,
  cart: initResource({
    cartId: null,
    currencyCode: '',
    discountAmount: 0,
    price: 0,
    taxAmount: 0,
    total: 0,
  }),
  applyPromoCode: initResource(null),
  removePromoCode: initResource(),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(getPaymentMethods.pending, state => {
      setPending(state.paymentMethods)
    })
    .addCase(getPaymentMethods.fulfilled, (state, { payload }) => {
      setSucceeded(state.paymentMethods, payload)
    })
    .addCase(getPaymentMethods.rejected, (state, { error: { message } }) => {
      setFailed(state.paymentMethods, message)
    })
    .addCase(selectProduct.pending, state => {
      setPending(state.cart)
    })
    .addCase(selectProduct.fulfilled, (state, { payload, meta: { arg } }) => {
      state.selectedProduct = arg
      state.applyPromoCode = initialState.applyPromoCode
      setSucceeded(state.cart, payload)
    })
    .addCase(selectProduct.rejected, (state, { error: { message } }) => {
      setFailed(state.cart, message)
    })
    .addCase(setSummaryFormValues, (state, { payload }) => {
      state.summaryFormValues = payload
    })
    .addCase(getAvailableSubscriptions.pending, state => {
      setPending(state.subscriptions)
    })
    .addCase(
      getAvailableSubscriptions.fulfilled,
      (state, { payload: subscriptions }) => {
        setSucceeded(
          state.subscriptions,
          subscriptions.map((s, idx) => ({
            featured: !idx,
            type: ProductType.Subscription,
            ...s,
          }))
        )
      }
    )
    .addCase(
      getAvailableSubscriptions.rejected,
      (state, { error: { message } }) => {
        setFailed(state.subscriptions, message)
      }
    )
    .addCase(getAdditionalPackages.pending, state => {
      setPending(state.packages)
    })
    .addCase(
      getAdditionalPackages.fulfilled,
      (state, { payload: packages }) => {
        setSucceeded(
          state.packages,
          packages.map((p, idx) => ({
            featured: !idx,
            type: ProductType.Package,
            ...p,
          }))
        )
      }
    )
    .addCase(
      getAdditionalPackages.rejected,
      (state, { error: { message } }) => {
        setFailed(state.packages, message)
      }
    )
    .addCase(applyPromoCode.pending, state => {
      setPending(state.applyPromoCode)
    })
    .addCase(
      applyPromoCode.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { couponName: promoCode },
          },
        }
      ) => {
        setSucceeded(state.applyPromoCode, { ...payload, promoCode })
      }
    )
    .addCase(applyPromoCode.rejected, (state, { error: { message } }) => {
      setFailed(state.applyPromoCode, message)
    })
    .addCase(removePromoCode.pending, state => {
      setPending(state.removePromoCode)
    })
    .addCase(removePromoCode.fulfilled, state => {
      state.applyPromoCode = initialState.applyPromoCode
      setSucceeded(state.removePromoCode)
    })
    .addCase(removePromoCode.rejected, (state, { error: { message } }) => {
      setFailed(state.removePromoCode, message)
    })
    .addCase(createOrder.pending, state => {
      setPending(state.createOrder)
    })
    .addCase(createOrder.fulfilled, state => {
      setSucceeded(state.createOrder)
    })
    .addCase(createOrder.rejected, (state, { error: { message } }) => {
      setFailed(state.createOrder, message)
    })
    .addCase(getAdministrativePaymentProduct.pending, state => {
      setPending(state.administrativePaymentProduct)
    })
    .addCase(
      getAdministrativePaymentProduct.fulfilled,
      (state, { payload }) => {
        setSucceeded(state.administrativePaymentProduct, payload)
      }
    )
    .addCase(
      getAdministrativePaymentProduct.rejected,
      (state, { error: { message } }) => {
        setFailed(state.administrativePaymentProduct, message)
      }
    )
)
